import { parseScalabilityMode, type types } from 'mediasoup-client';
import { serializeError } from 'serialize-error';
import { SpatialLayer, TemporalLayer } from '../constants/videoConstrains';
import filterStatsCodecs from '../helpers/filterStatsCodecs';
import type {
  ConsumerData,
  ExtendedRTCInboundRtpStreamStats, SpatialLayerParams,
  TrackInboundStats,
} from '../types/common';
import type Logger from './Logger';

const RESOLUTION_SCALING_FACTOR = 2;
const STATS_MAX_ATTEMPTS = 5;
const STATS_ATTEMPT_DELAY = 150;

export interface PeerConsumerDependencies {
  loggerFactory: (namespace: string) => Logger;
}

export interface PeerConsumerConstructorParams extends PeerConsumerDependencies {
  consumer: types.Consumer;
}

class PeerConsumer {
  public spatialLayers = SpatialLayer.LOW;

  public temporalLayers = TemporalLayer.BASE;

  public currentSpatialLayer = SpatialLayer.LOW;

  public currentTemporalLayer = TemporalLayer.BASE;

  public requestedSpatialLayer?: number;

  public currentMaxSpatialLayer: number;

  public availableSpatialLayers: SpatialLayerParams[] = [];

  public currentSpatialLayerParams?: SpatialLayerParams;

  readonly id: string;

  readonly kind: types.MediaKind;

  readonly producerId: string;

  readonly appData: ConsumerData;

  private readonly consumer: types.Consumer;

  readonly #logger: Logger;

  constructor(params: PeerConsumerConstructorParams) {
    const { consumer, loggerFactory } = params;
    this.id = consumer.id;
    this.kind = consumer.kind;
    this.producerId = consumer.producerId;
    this.appData = consumer.appData as unknown as ConsumerData;
    this.consumer = consumer;
    this.#logger = loggerFactory('PeerConsumer');
    this.currentMaxSpatialLayer = this.appData.producerData.maxSpatialLayer;
    this.parseScalabilityMode();
    this.initAvailableSpatialLayers();
    this.setCurrentSpatialLayerParams();
  }

  get paused(): boolean {
    return this.consumer.paused;
  }

  pause(): void {
    this.consumer.pause();
  }

  resume(): void {
    this.consumer.resume();
  }

  close(): void {
    this.consumer.close();
  }

  get isVideo(): boolean {
    return this.kind === 'video';
  }

  get isAudio(): boolean {
    return this.kind === 'audio';
  }

  parseScalabilityMode(): void {
    if (this.consumer.rtpParameters.encodings) {
      const { scalabilityMode } = this.consumer.rtpParameters.encodings[0];
      const { spatialLayers, temporalLayers } = parseScalabilityMode(scalabilityMode);
      this.spatialLayers = spatialLayers;
      this.temporalLayers = temporalLayers;
      this.#logger.debug('parseScalabilityMode()', {
        scalabilityMode,
        spatialLayers,
        temporalLayers,
      });
    }
  }

  initAvailableSpatialLayers(): void {
    const { encodings, trackTransformParams } = this.appData.producerData;

    if (!Array.isArray(encodings)) {
      return;
    }

    this.availableSpatialLayers = encodings
      .map((encoding): SpatialLayerParams => this
        .tryPrepareSpatialLayerParams(encoding));

    if (!trackTransformParams || this.availableSpatialLayers.length > 1 || this.spatialLayers < SpatialLayer.HIGH) {
      this.#logger.debug('Using available spatial layers', { case: 'initAvailableSpatialLayers' });

      return;
    }

    this.#logger.debug('Pre-generating encodings with spatial layer params', { case: 'initAvailableSpatialLayers' });

    const preGeneratedEncodings: SpatialLayerParams[] = [];
    const baseEncoding = encodings[encodings.length - 1];

    // imitate encodings with spatial layers params for automatic quality switching on client side
    for (let i = this.spatialLayers - 1; i >= 0; i -= 1) {
      preGeneratedEncodings.push(this.tryPrepareSpatialLayerParams({
        ...baseEncoding,
        scaleResolutionDownBy: RESOLUTION_SCALING_FACTOR ** i,
      }));
    }

    this.availableSpatialLayers = preGeneratedEncodings;
  }

  setCurrentSpatialLayerParams(): void {
    const { encodings } = this.appData.producerData;

    if (!encodings?.[this.currentSpatialLayer]) {
      return;
    }

    this.currentSpatialLayerParams = this.tryPrepareSpatialLayerParams(encodings[this.currentSpatialLayer]);
    this.#logger.debug('setCurrentSpatialLayerParams()', { currentSpatialLayerParams: this.currentSpatialLayerParams });
  }

  tryPrepareSpatialLayerParams(encoding: types.RtpEncodingParameters): SpatialLayerParams {
    const { trackTransformParams } = this.appData.producerData;

    if (!trackTransformParams) {
      return encoding;
    }

    const { width, height } = trackTransformParams;
    const coefficient = encoding.scaleResolutionDownBy ?? 1;

    if (width && height) {
      return {
        width: width / coefficient,
        height: height / coefficient,
        ...encoding,
      };
    }

    return encoding;
  }

  setCurrentSpatialLayer(currentSpatialLayer: number): void {
    if (!this.isVideo) {
      throw new Error('setCurrentSpatialLayer applies to video consumers only');
    }

    this.currentSpatialLayer = currentSpatialLayer;
    this.#logger.debug('setCurrentSpatialLayer()', { currentSpatialLayer });
    this.setCurrentSpatialLayerParams();
  }

  setCurrentTemporalLayer(currentTemporalLayer: number): void {
    if (!this.isVideo) {
      throw new Error('setCurrentTemporalLayer applies to video consumers only');
    }

    this.currentTemporalLayer = currentTemporalLayer;
    this.#logger.debug('setCurrentTemporalLayer()', { currentTemporalLayer });
  }

  applyLayersUpdate(payload: {
    spatialLayer?: number;
    temporalLayer?: number;
  }): void {
    if (!this.isVideo) {
      throw new Error('applyLayersUpdate applies to video consumers only');
    }

    const { spatialLayer, temporalLayer } = payload;

    if (spatialLayer !== undefined) {
      this.setCurrentSpatialLayer(spatialLayer);
    }

    if (temporalLayer !== undefined) {
      this.setCurrentTemporalLayer(temporalLayer);
    }
  }

  setRequestedSpatialLayer(spatialLayer: number | undefined): void {
    this.requestedSpatialLayer = spatialLayer;
  }

  setCurrentMaxSpatialLayer(spatialLayer: number): void {
    this.currentMaxSpatialLayer = spatialLayer;
  }

  async getStats(): Promise<TrackInboundStats | undefined> {
    try {
      return {
        consumerId: this.consumer.id,
        codec: this.consumer.rtpParameters.codecs.find((codec) => filterStatsCodecs(codec)),
        dtlsState: this.consumer.rtpReceiver?.transport?.state,
        availableSpatialLayers: this.availableSpatialLayers,
        currentSpatialLayerParams: this.currentSpatialLayerParams,
        requestedSpatialLayer: this.requestedSpatialLayer,
        rtcStats: await this.getInboundRTPStreamStats(),
      };
    } catch (error) {
      this.#logger.debug('getStats()', { error });

      return undefined;
    }
  }

  async getInboundRTPStreamStats(): Promise<ExtendedRTCInboundRtpStreamStats> {
    return await new Promise((resolve, reject) => {
      const getRTCStatsReport = async (attempt = 0): Promise<void> => {
        try {
          const rtcStatsReport = await this.consumer.getStats();
          const inboundRTPStreamStats: ExtendedRTCInboundRtpStreamStats = Array.from(rtcStatsReport.values())
            .find((stat) => stat.type === 'inbound-rtp');

          if (!inboundRTPStreamStats && attempt < STATS_MAX_ATTEMPTS) {
            setTimeout(() => {
              getRTCStatsReport(attempt + 1).catch((error: unknown) => {
                this.#logger.debug('Error getting RTCStatsReport', {
                  attempt,
                  error: serializeError(error),
                });
              });
            }, STATS_ATTEMPT_DELAY);

            return;
          }

          if (attempt >= STATS_MAX_ATTEMPTS && !inboundRTPStreamStats) {
            reject(new Error('InboundRTPStreamStat not exist'));
          }

          resolve(inboundRTPStreamStats);
        } catch (error) {
          reject(new Error('Can not get RTCStatsReport'));
          this.#logger.debug('getInboundRTPStreamStats()', { error });
        }
      };

      getRTCStatsReport().catch((error: unknown) => {
        this.#logger.debug('Error executing getRTCStatsReport', {
          case: 'PeerConsumer',
          error: serializeError(error),
        });
      });
    });
  }
}

export default PeerConsumer;
